import { createSlice } from '@reduxjs/toolkit'
import {
  getMarkets,
  getMarketDetail,
  getOrderbook,
  placeBet,
  cancelBet,
  getUserBets,
} from 'redux-thunk/thunk/predictionMarket.thunk'

const initialState = {
  markets: [],
  marketsTotal: 0,
  selectedMarket: null,
  priceHistory: [],
  orderbook: {
    yes: { bids: [], asks: [] },
    no: { bids: [], asks: [] },
    lastPrice: null,
    timestamp: null,
  },
  myBets: [],
  myBetsTotal: 0,
  myPositions: [],
  filters: {
    category: 'all',
    status: 'active',
    search: '',
  },
  pagination: { limit: 20, offset: 0 },
  loading: {
    markets: false,
    market: false,
    orderbook: false,
    placeBet: false,
    myBets: false,
  },
  error: null,
  betSuccess: null,
}

const {
  actions: {
    setFilters,
    setPagination,
    updateOrderbookFromSocket,
    updatePriceFromSocket,
    appendPriceTick,
    applyPriceChange,
    clearBetSuccess,
    clearError,
    clearSelectedMarket,
  },
  reducer,
} = createSlice({
  name: 'predictionMarket',
  initialState,
  reducers: {
    setFilters: (state, action) => ({ ...state, filters: { ...state.filters, ...action.payload } }),
    setPagination: (state, action) => ({ ...state, pagination: { ...state.pagination, ...action.payload } }),
    clearSelectedMarket: (state) => ({ ...state, selectedMarket: null, priceHistory: [], orderbook: initialState.orderbook }),
    clearBetSuccess: (state) => ({ ...state, betSuccess: null }),
    clearError: (state) => ({ ...state, error: null }),

    // Called by socket event handlers
    updateOrderbookFromSocket: (state, action) => {
      const { tokenId, bids, asks, timestamp } = action.payload
      if (!state.selectedMarket) return state
      const isYes = tokenId === state.selectedMarket?.yesTokenId
      const isNo  = tokenId === state.selectedMarket?.noTokenId
      // Discard updates that don't belong to the currently viewed market
      if (!isYes && !isNo) return state
      const side = isYes ? 'yes' : 'no'
      return {
        ...state,
        orderbook: {
          ...state.orderbook,
          [side]: { bids, asks },
          timestamp,
          synthetic: false,
        },
      }
    },

    updatePriceFromSocket: (state, action) => {
      const { price, timestamp } = action.payload
      const parsed = parseFloat(price)
      if (isNaN(parsed)) return state
      return {
        ...state,
        orderbook: { ...state.orderbook, lastPrice: parsed, timestamp },
      }
    },

    appendPriceTick: (state, action) => {
      const { t, price } = action.payload
      const last = state.priceHistory[state.priceHistory.length - 1]
      if (last && Math.abs(last.price - price) < 0.0001) return state
      return { ...state, priceHistory: [...state.priceHistory.slice(-500), { t, price }] }
    },

    // Apply a single price-level delta from Polymarket price_change WS event
    applyPriceChange: (state, action) => {
      const { tokenId, price, size, side } = action.payload
      if (!state.selectedMarket) return state
      const isYes = tokenId === state.selectedMarket?.yesTokenId
      const isNo  = tokenId === state.selectedMarket?.noTokenId
      if (!isYes && !isNo) return state

      const bookKey  = isYes ? 'yes' : 'no'
      const book     = state.orderbook[bookKey] || { bids: [], asks: [] }
      // Polymarket: BUY orders go into bids, SELL orders go into asks
      const levelKey = side === 'BUY' ? 'bids' : 'asks'
      const levels   = [...(book[levelKey] || [])]
      const idx      = levels.findIndex(l => Math.abs(parseFloat(l.price) - price) < 0.000001)

      if (size === 0) {
        if (idx >= 0) levels.splice(idx, 1)
      } else if (idx >= 0) {
        levels[idx] = { price, size }
      } else {
        levels.push({ price, size })
      }

      return {
        ...state,
        orderbook: {
          ...state.orderbook,
          [bookKey]: { ...book, [levelKey]: levels },
          synthetic: false,
        },
      }
    },
  },

  extraReducers: (builder) => {
    // getMarkets
    builder
      .addCase(getMarkets.pending, (state) => ({ ...state, loading: { ...state.loading, markets: true }, error: null }))
      .addCase(getMarkets.fulfilled, (state, action) => ({
        ...state,
        loading: { ...state.loading, markets: false },
        markets: action.payload?.rows || [],
        marketsTotal: action.payload?.count || 0,
      }))
      .addCase(getMarkets.rejected, (state, action) => ({
        ...state,
        loading: { ...state.loading, markets: false },
        error: action.payload,
      }))

    // getMarketDetail
    builder
      .addCase(getMarketDetail.pending, (state) => ({ ...state, loading: { ...state.loading, market: true }, error: null }))
      .addCase(getMarketDetail.fulfilled, (state, action) => ({
        ...state,
        loading: { ...state.loading, market: false },
        selectedMarket: action.payload?.market || null,
        orderbook: action.payload?.orderbook || initialState.orderbook,
        priceHistory: action.payload?.priceHistory || [],
      }))
      .addCase(getMarketDetail.rejected, (state, action) => ({
        ...state,
        loading: { ...state.loading, market: false },
        error: action.payload,
      }))

    // getOrderbook
    builder
      .addCase(getOrderbook.fulfilled, (state, action) => ({
        ...state,
        loading: { ...state.loading, orderbook: false },
        orderbook: { ...state.orderbook, ...action.payload?.orderbook },
      }))

    // placeBet
    builder
      .addCase(placeBet.pending, (state) => ({ ...state, loading: { ...state.loading, placeBet: true }, error: null, betSuccess: null }))
      .addCase(placeBet.fulfilled, (state, action) => ({
        ...state,
        loading: { ...state.loading, placeBet: false },
        betSuccess: action.payload,
      }))
      .addCase(placeBet.rejected, (state, action) => ({
        ...state,
        loading: { ...state.loading, placeBet: false },
        error: action.payload,
      }))

    // cancelBet
    builder
      .addCase(cancelBet.fulfilled, (state, action) => {
        const cancelledId = action.payload?.betId
        return {
          ...state,
          myBets: state.myBets.map(b =>
            b.id === cancelledId ? { ...b, status: 'cancelled' } : b
          ),
        }
      })

    // getUserBets
    builder
      .addCase(getUserBets.pending, (state) => ({ ...state, loading: { ...state.loading, myBets: true } }))
      .addCase(getUserBets.fulfilled, (state, action) => ({
        ...state,
        loading: { ...state.loading, myBets: false },
        myBets: action.payload?.bets?.rows || [],
        myBetsTotal: action.payload?.bets?.count || 0,
        myPositions: action.payload?.positions || [],
      }))
      .addCase(getUserBets.rejected, (state, action) => ({
        ...state,
        loading: { ...state.loading, myBets: false },
        error: action.payload,
      }))
  },
})

export {
  setFilters,
  setPagination,
  updateOrderbookFromSocket,
  updatePriceFromSocket,
  appendPriceTick,
  applyPriceChange,
  clearBetSuccess,
  clearError,
  clearSelectedMarket,
}
export default reducer
